import { ViewRoom } from '@xeetr/types/models'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import RoomContent from './RoomContent'

import { RoomFormProps } from '@/types/RoomForm'

/**
 * Individual room form with independent FormProvider
 * Each accordion item is its own form
 */
const RoomForm = ({
  room,
  isExpanded,
  onRegisterForm,
  onUpdate,
  onCopy,
  onDelete,
}: RoomFormProps) => {
  const methods = useForm<ViewRoom>({
    defaultValues: room,
    mode: 'onChange', // Validate on change for immediate feedback
  })

  const { formState, handleSubmit, reset } = methods

  // Register/unregister form control when expanded state changes
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        roomId: room.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          // Reset form with new data to mark as clean
          reset(data)
        }),
        onDiscard: () => {
          reset(room) // Reset to original room data
        },
      }
      onRegisterForm(formHandle)
    } else {
      onRegisterForm(null)
    }
  }, [isExpanded, formState.isDirty, formState.isSubmitting, room.id])

  // Update form control when dirty state changes (user types)
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        roomId: room.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          reset(data)
        }),
        onDiscard: () => {
          reset(room)
        },
      }
      onRegisterForm(formHandle)
    }
  }, [formState.isDirty, formState.isSubmitting])

  // Sync form with external room changes (e.g., after successful save)
  useEffect(() => {
    reset(room)
  }, [room])

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()}>
        <RoomContent onCopy={onCopy} onDelete={onDelete} />
      </form>
    </FormProvider>
  )
}

export default RoomForm
